import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import { createSelector } from 'reselect';

import moment from 'moment';

import {
  MapContainer,
  TileLayer,
  Marker,
  ZoomControl,
  GeoJSON,
} from 'react-leaflet';

import Leaflet from 'leaflet';

// Import custom SVG icons extension
import '../utils/svg-icons';

import {
  getWindowHeight,
  HEADER_HEIGHT,
  DRAWER_WIDTH,
  DRAWER_WIDTH_SMALL,
} from '../utils';
import { PlantType } from '../utils/types';

import { plantsSelector } from '../store/plants';
import { reactorsOfPlantSelector } from '../store/reactors';
import { riversSelector } from '../store/rivers';

const ICON_OPTIONS = {
  fillOpacity: 1,
  iconSize: [24, 38],
  circleColor: 'white',
  circleRatio: 0.6,
  circleWeight: 3,
};

function hasNotif(plant) {
  return plant.reactors.reduce((res, reactor) => {
    if (reactor.status !== 'RUNNING' && reactor.status !== 'PLANNED_STOP') {
      return true;
    }
    return res;
  }, false);
}

function plantRatio(plant, currentDate) {
  const hourIndex = Math.max(moment(currentDate).hours(), 0);

  // Handle case where plant has no reactors
  if (!plant.reactors || plant.reactors.length === 0) {
    return 0;
  }

  const totalProd = plant.reactors.reduce((res, reactor) => {
    // Handle case where dayProductions is empty or not yet loaded
    if (!reactor.dayProductions || reactor.dayProductions.length === 0) {
      return res;
    }

    const lastIndex = reactor.dayProductions.reduce(
      (last, v, i) =>
        v && v.value !== null && v.value !== undefined ? i : last,
      0,
    );
    const index = Math.min(lastIndex, hourIndex);

    // Safety check for array bounds and null values
    if (
      index >= 0 &&
      index < reactor.dayProductions.length &&
      reactor.dayProductions[index]
    ) {
      const prod =
        reactor.dayProductions[index].value !== null &&
        reactor.dayProductions[index].value !== undefined
          ? Math.max(reactor.dayProductions[index].value, 0)
          : 0;
      return res + prod;
    }

    return res;
  }, 0);

  const totalPower = plant.reactors.reduce(
    (res, r) => res + (r.netPower_MW || 0),
    0,
  );

  return totalPower > 0 ? Math.min(totalProd / totalPower, 1) : 0; // Cap at 100%
}

// Create a stable selector for plants data that avoids object creation
const selectPlantsForDate = createSelector(
  [state => state, (state, currentDate) => currentDate],
  (state, currentDate) => plantsSelector(state, { date: currentDate }),
);

function PlantMap({
  currentPlantId = null,
  onPlantClick,
  drawerHeight,
  currentDate,
}) {
  // Use stable selector to avoid recreating objects
  const plantsData = useSelector(state =>
    selectPlantsForDate(state, currentDate),
  );
  const rivers = useSelector(riversSelector);

  // Get all reactor and production data
  const allReactorsData = useSelector(state => state.reactors?.data || {});
  const productionsState = useSelector(
    state =>
      state.productions?.[moment(currentDate).format('YYYY-MM-DD')] || null,
  );

  // Memoize plants with reactors to prevent unnecessary re-renders
  const plants = useMemo(() => {
    if (!plantsData || plantsData.length === 0) {
      console.log('PlantMap: No plants data available');
      return [];
    }

    console.log('PlantMap: Processing plants data', {
      plantsCount: plantsData.length,
      reactorsCount: Object.keys(allReactorsData).length,
      hasProductionData: !!productionsState,
      currentDate,
    });

    return plantsData.map(plant => {
      // Get reactors for this plant and add production data
      const plantReactors = Object.values(allReactorsData)
        .filter(reactor => reactor.plantId === plant.id)
        .map(reactor => {
          // Get production data for this reactor
          const productionData = productionsState?.data?.[reactor.eicCode];
          const dayProductions = productionData?.values
            ? productionData.values.map(d => ({
                value: Math.max(d.value || 0, 0),
              }))
            : Array.from({ length: 24 }, () => ({ value: 0 })); // Default to 24 hours of zero production

          return {
            ...reactor,
            status: 'RUNNING', // For now, assume all reactors are running for map display
            dayProductions,
          };
        })
        .sort((a, b) => a.reactorIndex - b.reactorIndex);

      const loadRatio = plantRatio(
        { ...plant, reactors: plantReactors },
        currentDate,
      );
      console.log(
        `Plant ${plant.name}: ${
          plantReactors.length
        } reactors, load ratio: ${loadRatio.toFixed(2)}`,
      );

      return {
        ...plant,
        reactors: plantReactors,
      };
    });
  }, [plantsData, allReactorsData, productionsState, currentDate]);

  const isSmallScreen = window.innerWidth <= 750;
  const height = getWindowHeight();
  const leftPosition = isSmallScreen
    ? Math.floor(window.innerWidth * 0.5)
    : DRAWER_WIDTH; // Use 50% width on small screens, fixed width on large screens

  // Debug logging
  console.log('PlantMap debug:', {
    isSmallScreen,
    leftPosition,
    windowWidth: window.innerWidth,
    drawerHeight,
  });

  // Memoize plant markers to prevent unnecessary re-creation
  const plantMarkers = useMemo(() => {
    return plants.map(plant => (
      <Marker
        key={plant.id}
        title={plant.name}
        position={plant.coords}
        icon={
          plant.id === currentPlantId
            ? new Leaflet.DivIcon.SVGIcon.IndicatorIcon({
                ...ICON_OPTIONS,
                color: 'red',
                loadRate: plantRatio(plant, currentDate),
                notif: hasNotif(plant),
              })
            : new Leaflet.DivIcon.SVGIcon.IndicatorIcon({
                ...ICON_OPTIONS,
                loadRate: plantRatio(plant, currentDate),
                notif: hasNotif(plant),
              })
        }
        eventHandlers={{
          click: () => onPlantClick(plant),
        }}
      />
    ));
  }, [plants, currentPlantId, currentDate, onPlantClick]);

  // Memoize river overlays
  const riverOverlays = useMemo(() => {
    return rivers.map(river => <GeoJSON key={river.name} data={river} />);
  }, [rivers]);

  return (
    <MapContainer
      center={[48.3, 2]}
      zoom={isSmallScreen ? 5 : 6}
      style={{
        position: 'fixed',
        top: HEADER_HEIGHT,
        right: 0,
        bottom: 0,
        left: isSmallScreen ? '50vw' : leftPosition, // Use CSS unit on small screens, JS calculation on large screens
        marginTop: 0,
        zIndex: 1, // Ensure map is above other content but below header
        width: isSmallScreen ? '50vw' : 'auto', // Set width only on small screens
      }}
      zoomControl={false}
      className="map-custom-cursor"
    >
      <ZoomControl position="bottomright" />
      <TileLayer
        url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        attribution='&copy; <a href="http://osm.org/copyright">OpenStreetMap</a> contributors'
      />
      {plantMarkers}
      {riverOverlays}
    </MapContainer>
  );
}

PlantMap.propTypes = {
  currentPlantId: PropTypes.string,
  onPlantClick: PropTypes.func.isRequired,
  drawerHeight: PropTypes.number.isRequired,
  currentDate: PropTypes.string.isRequired,
};

export default PlantMap;
